import { useCallback, useRef } from "react";
import { confirmActionPlanAiPlan } from "@/components/common/ActionPlanV2/actionPlanAiConfirm";
import { buildActionPlanPlanFromTaskPick } from "@/components/common/ActionPlanV2/actionPlanAiTaskList";
import { isBscStepPlan, type ActionPlanAiPendingPlan } from "@/types/action-plan-v2/ActionPlanAiPlan";
import { getAiStepSkill } from "@/components/common/AiStepAssistant/aiStepSkillRegistry";
import type { ActionPlanAiPendingState } from "@/types/action-plan-v2/ActionPlanAiPlan";
import type {
  ActionPlanAiTaskListState,
  ActionPlanAiTaskPickAction,
} from "@/types/action-plan-v2/ActionPlanAiTaskList";

type ChatSlice = {
  turns: {
    id: string;
    actionPlanPending?: ActionPlanAiPendingState;
    actionPlanPendingQueue?: ActionPlanAiPendingState[];
    actionPlanTaskList?: ActionPlanAiTaskListState;
    actionPlanObjectiveList?: import("@/types/action-plan-v2/ActionPlanAiObjectiveList").ActionPlanAiObjectiveListState;
  }[];
  updateActionPlanPending: (turnId: string, pending: ActionPlanAiPendingState) => void;
  updateActionPlanTaskList?: (turnId: string, state: ActionPlanAiTaskListState) => void;
  advanceActionPlanPendingQueue?: (turnId: string) => void;
};

/** Xác nhận thẻ tạo việc từ chat (Floating + Hub bubble). */
export function useActionPlanAiChatConfirm(chat: ChatSlice) {
  const turnsRef = useRef(chat.turns);
  turnsRef.current = chat.turns;
  const updateRef = useRef(chat.updateActionPlanPending);
  updateRef.current = chat.updateActionPlanPending;

  return useCallback(async (turnId: string, planOverride?: ActionPlanAiPendingPlan) => {
    const turn = turnsRef.current.find((t) => t.id === turnId);
    const pending = turn?.actionPlanPending;
    if (!pending || pending.status !== "pending") return;
    const plan = planOverride ?? pending.plan;
    const update = updateRef.current;
    update(turnId, { ...pending, plan, status: "confirming" });
    try {
      const resultId = await confirmActionPlanAiPlan(plan, {
        runId: pending.runId,
        interruptId: pending.interruptId,
        edited: planOverride !== undefined,
      });
      update(turnId, { ...pending, plan, status: "done", resultId });
      // Dữ liệu B1–B5 vừa ghi: trang bước đang mở tải lại (cùng hook «Áp dụng đề xuất»)
      if (isBscStepPlan(plan)) getAiStepSkill()?.onApplied?.();
      chat.advanceActionPlanPendingQueue?.(turnId);
    } catch (e) {
      update(turnId, {
        ...pending,
        plan,
        status: "error",
        error: e instanceof Error ? e.message : "Không lưu được.",
      });
    }
  }, [chat]);
}

/** Chọn mục tiêu trên thẻ — mở thẻ tạo việc gốc. */
export function useActionPlanAiObjectivePick(chat: ChatSlice) {
  const turnsRef = useRef(chat.turns);
  turnsRef.current = chat.turns;
  const updatePendingRef = useRef(chat.updateActionPlanPending);
  updatePendingRef.current = chat.updateActionPlanPending;

  return useCallback(async (turnId: string, objectiveId: string) => {
    const turn = turnsRef.current.find((t) => t.id === turnId);
    const listState = turn?.actionPlanObjectiveList;
    if (!listState?.list) return;
    const { buildActionPlanPlanFromObjectivePick } = await import(
      "@/components/common/ActionPlanV2/actionPlanAiObjectiveList"
    );
    const plan = buildActionPlanPlanFromObjectivePick(listState.list, objectiveId);
    updatePendingRef.current(turnId, { plan, status: "pending" });
  }, []);
}

/** Bấm Sửa / Việc con trên thẻ danh sách — mở thẻ draft không cần thêm lượt chat. */
export function useActionPlanAiTaskListPick(chat: ChatSlice) {
  const turnsRef = useRef(chat.turns);
  turnsRef.current = chat.turns;
  const updatePendingRef = useRef(chat.updateActionPlanPending);
  updatePendingRef.current = chat.updateActionPlanPending;

  return useCallback(async (turnId: string, action: ActionPlanAiTaskPickAction, taskId: string) => {
    const turn = turnsRef.current.find((t) => t.id === turnId);
    const listState = turn?.actionPlanTaskList;
    if (!listState?.list) return;
    try {
      const plan = await buildActionPlanPlanFromTaskPick(listState.list, taskId, action);
      updatePendingRef.current(turnId, { plan, status: "pending" });
    } catch (e) {
      updatePendingRef.current(turnId, {
        plan: {
          action: "manual",
          mustManual: true,
          reason: e instanceof Error ? e.message : "Không mở được thẻ việc.",
        },
        status: "pending",
      });
    }
  }, []);
}

/** «Xác nhận tất cả» việc con: xác nhận thẻ hiện tại, hàng đợi tự chuyển thẻ kế; dừng khi lỗi hoặc hết. */
export function useActionPlanAiChatConfirmAll(
  chat: ChatSlice,
  confirm: (turnId: string, planOverride?: ActionPlanAiPendingPlan) => Promise<void>,
) {
  const turnsRef = useRef(chat.turns);
  turnsRef.current = chat.turns;

  return useCallback(async (turnId: string) => {
    for (let guard = 0; guard < 20; guard++) {
      const pending = turnsRef.current.find((t) => t.id === turnId)?.actionPlanPending;
      if (!pending || pending.status !== "pending") return;
      await confirm(turnId);
      // Chờ React cập nhật lượt (hàng đợi chuyển sang thẻ kế) trước khi đọc lại
      await new Promise((r) => window.setTimeout(r, 120));
      const after = turnsRef.current.find((t) => t.id === turnId)?.actionPlanPending;
      if (!after || after.status === "error") return;
    }
  }, [confirm]);
}
